import style from './index.module.scss';

import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useInstallable } from '@context/Installable';

import StickyMenu from './StickyMenu';
import DefaultMenu from './Default';
import ProspectusLite from './ProspectusLite';
import Link from 'next/link';

import { hasAlphaLessThanOne } from '@lib/helpers';

import { CDN_URL, VERSION_LITE, MENU_STICKY, DOWNLOAD_PROSPECTUS_BUTTON_TEXT } from '@lib/constants';

export default function MastHead({ isSecondaryStyle = false, hideMenuItems = false }) {
	const [isAdvisorView, setAdvisorView] = useState(false);
	const [mastheadIsTransparent, setMastheadIsTransparent] = useState(false);

	const { promptable, promptToInstall, isInstalled } = useInstallable();

	const router = useRouter();

	const classes = {
		'/everything': 'everything',
		'/personalise': 'personalise',
		'/basket': 'personalise',
		'/advisor': 'personalise',
		'/overview': 'overview',
		'/share': 'personalise',
		'/auth/login': 'overview',
		'/auth/register': 'overview',
		'/course/[slug]': 'overview',
		'/page/[slug]': 'overview',
		'/prospectus/[slug]': 'overview',
		'/event/[slug]': 'overview',
		'/configure': 'overview',
	};

	const headerLogos = {
		'/personalise': CDN_URL + '/logo-header.webp',
		'/share': CDN_URL + '/logo-header.webp',
		'/overview': CDN_URL + '/overview-header-logo.webp',
		'/prospectus/[slug]': CDN_URL + '/overview-header-logo.webp',
		'/course/[slug]': CDN_URL + '/overview-header-logo.webp',
		'/page/[slug]': CDN_URL + '/overview-header-logo.webp',
		'/event/[slug]': CDN_URL + '/overview-header-logo.webp',
		'/configure': CDN_URL + '/overview-header-logo.webp',
	};

	// Is the current view a view from an Advisor point of view?
	useEffect(() => {
		const url = window.location.href;
		const urlParams = new URLSearchParams(new URL(url).search);
		const isAdvisor = urlParams.get('isAdvisorView') ? urlParams.get('isAdvisorView') : '';

		if (isAdvisor) {
			setAdvisorView(true);
		}
	}, []);

	useEffect(() => {
		let headerBackgroundColor = getComputedStyle(document.documentElement).getPropertyValue(
			'--overview-header-background-colour'
		);

		switch (classes[router.pathname]) {
			case 'personalise':
				headerBackgroundColor = getComputedStyle(document.documentElement).getPropertyValue(
					'--personalisation-header-background-colour'
				);
				break;
			case 'everything':
				headerBackgroundColor = getComputedStyle(document.documentElement).getPropertyValue(
					'--everything-header-background-colour'
				);
				break;
		}

		setMastheadIsTransparent(hasAlphaLessThanOne(headerBackgroundColor));
	}, [router.pathname]);

	return (
		<header
			className={`columns masthead ${style.masthead} ${mastheadIsTransparent ? style.isTransparent : ''} ${isSecondaryStyle && style.isSecondaryStyle} ${'true' === MENU_STICKY ? style.isSticky : ''} ${classes[router.pathname] ? style[classes[router.pathname]] : ''}`}
		>
			<div className="masthead__container column pt-1 pb-1 pl-1 pr-1 is-12 is-flex is-align-items-center is-justify-content-space-between">
				<div className={`masthead__left ${style.leftContain}`}>
					<Link aria-label="Return to the prospectus homepage" href="/" className={style.logoLink}>
						<img
							src={
								headerLogos[router.pathname]
									? headerLogos[router.pathname]
									: CDN_URL + '/logo-header.webp'
							}
							alt="Prospectus logo"
							height={40}
							className={`masthead__logo ${style.logo}`}
						/>
					</Link>

					{!VERSION_LITE && !isInstalled && promptable && (
						<button className={`tooltip ${style.downloadBtn}`} onClick={promptToInstall}>
							{DOWNLOAD_PROSPECTUS_BUTTON_TEXT}
							<svg viewBox="0 0 512 512">
								<path d="M480 352h-133.5l-45.25 45.25C289.2 409.3 273.1 416 256 416s-33.16-6.656-45.25-18.75L165.5 352H32c-17.67 0-32 14.33-32 32v96c0 17.67 14.33 32 32 32h448c17.67 0 32-14.33 32-32v-96C512 366.3 497.7 352 480 352zM432 456c-13.2 0-24-10.8-24-24c0-13.2 10.8-24 24-24s24 10.8 24 24C456 445.2 445.2 456 432 456zM233.4 374.6C239.6 380.9 247.8 384 256 384s16.38-3.125 22.62-9.375l128-128c12.49-12.5 12.49-32.75 0-45.25c-12.5-12.5-32.76-12.5-45.25 0L288 274.8V32c0-17.67-14.33-32-32-32C238.3 0 224 14.33 224 32v242.8L150.6 201.4c-12.49-12.5-32.75-12.5-45.25 0c-12.49 12.5-12.49 32.75 0 45.25L233.4 374.6z" />
							</svg>
							<span className="tooltip-text">Download your prospectus & view your choices offline</span>
						</button>
					)}

					{!VERSION_LITE && isInstalled && (
						<span className={`tooltip ${style.downloadBtn}`} tabIndex="0">
							Available offline
							<svg viewBox="0 0 448 512">
								<path d="M438.6 105.4C451.1 117.9 451.1 138.1 438.6 150.6L182.6 406.6C170.1 419.1 149.9 419.1 137.4 406.6L9.372 278.6C-3.124 266.1-3.124 245.9 9.372 233.4C21.87 220.9 42.13 220.9 54.63 233.4L159.1 338.7L393.4 105.4C405.9 92.88 426.1 92.88 438.6 105.4H438.6z" />
							</svg>
							<span className="tooltip-text">
								You can view your prospectus without an internet connection.
							</span>
						</span>
					)}
				</div>

				{!VERSION_LITE && !hideMenuItems && 'true' === MENU_STICKY ? (
					<StickyMenu isSecondaryStyle={isSecondaryStyle} isAdvisorView={isAdvisorView} />
				) : (
					<DefaultMenu isAdvisorView={isAdvisorView} />
				)}
				{VERSION_LITE && !hideMenuItems ? <ProspectusLite isAdvisorView={isAdvisorView} /> : ''}
			</div>
		</header>
	);
}
